import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createClient, isAuthRetryableFetchError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

function isolatedAuth(scope: string) {
  return {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    storageKey: `integritty-${scope}`,
  } as const;
}

// Per-session Supabase clients for non-active session queries / Realtime
const _sessionClients = new Map<string, ReturnType<typeof createClient<any>>>();

export function getSessionClient(sess: StoredSession): ReturnType<typeof createClient<any>> {
  const key = `${sess.user_id}:${sess.access_token}`;
  const existing = _sessionClients.get(key);
  if (existing) return existing;
  // Evict stale token entries for this user
  for (const k of [..._sessionClients.keys()]) {
    if (k.startsWith(`${sess.user_id}:`)) _sessionClients.delete(k);
  }
  const client = createClient(
    import.meta.env.VITE_SUPABASE_URL as string,
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
    {
      auth: isolatedAuth(`probe-${sess.user_id}`),
      global: { headers: { Authorization: `Bearer ${sess.access_token}` } },
    },
  );
  void client.realtime.setAuth(sess.access_token);
  _sessionClients.set(key, client);
  return client;
}

export function evictSessionClient(userId: string) {
  for (const k of [..._sessionClients.keys()]) {
    if (k.startsWith(`${userId}:`)) _sessionClients.delete(k);
  }
}

// ── Cross-session workspace cache ─────────────────────────────────────────────
// Module-level (not persisted); rebuilt as workspaces are fetched for each session.
interface WsEntry { id: string; slug: string; name: string; status: string | null; userId: string }
const _wsCache = new Map<string, WsEntry>(); // key = workspaceId

export function registerWorkspacesForSession(
  userId: string,
  workspaces: { id: string; slug: string; name: string; status?: string | null }[],
) {
  for (const ws of workspaces) {
    _wsCache.set(ws.id, {
      id: ws.id, slug: ws.slug, name: ws.name,
      status: ws.status ?? null, userId,
    });
  }
}

function evictWorkspacesForSession(userId: string) {
  for (const [workspaceId, workspace] of _wsCache) {
    if (workspace.userId === userId) _wsCache.delete(workspaceId);
  }
}

export function getAllCachedWorkspaces(): WsEntry[] {
  return [..._wsCache.values()];
}

export function getWorkspaceOwnerUserId(workspaceId: string): string | undefined {
  return _wsCache.get(workspaceId)?.userId;
}

export function getWorkspaceSlug(workspaceId: string): string | undefined {
  return _wsCache.get(workspaceId)?.slug;
}

export function getWorkspaceName(workspaceId: string): string | undefined {
  return _wsCache.get(workspaceId)?.name;
}

export type EnsureWorkspaceResult =
  | { ok: true }
  | { stale: true; email: string }
  | { ok: false };

/**
 * Authoritative gate for workspace access.
 *
 * - Returns { ok: true } if the active session (or a non-stale one we can switch to) is a member.
 *   Side-effect: switches the active session when needed.
 * - Returns { stale: true, email } if the only matching session needs re-authentication.
 * - Returns { ok: false } if no stored session has access. Callers MUST block navigation.
 */
export async function ensureSessionForWorkspace(workspaceSlug: string): Promise<EnsureWorkspaceResult> {
  const store = useSessionStore.getState();
  const { sessions, activeUserId } = store;

  // 1. Probe the active session's DB via RLS.
  //    The workspaces SELECT policy also exposes rows where owner_user_id = auth.uid(),
  //    even if the user has no workspace_members row. We therefore do a second check
  //    against workspace_members (whose RLS only returns rows if the caller is an actual
  //    member) to confirm real membership before short-circuiting.
  const { data: activeData } = await (supabase as any)
    .from('workspaces')
    .select('id, slug, name, status')
    .eq('slug', workspaceSlug)
    .maybeSingle();
  if (activeData?.id) {
    // Confirm active user is an actual member — workspace_members RLS only returns rows
    // if user_is_workspace_member(auth.uid(), workspace_id) is true.
    const { data: memberRow } = await (supabase as any)
      .from('workspace_members')
      .select('user_id')
      .eq('workspace_id', activeData.id)
      .eq('user_id', activeUserId)
      .maybeSingle();
    if (memberRow?.user_id) {
      registerWorkspacesForSession(activeUserId ?? '', [activeData]);
      return { ok: true };
    }
    // Active session can see the workspace (e.g. as owner_user_id) but is not a member —
    // fall through to probe other sessions.
  }

  // 2. Active session is not a member — probe non-active non-stale sessions.
  for (const sess of sessions.filter((s) => s.user_id !== activeUserId && !s.stale)) {
    const client = getSessionClient(sess) as any;
    const { data } = await client
      .from('workspaces')
      .select('id, slug, name, status')
      .eq('slug', workspaceSlug)
      .maybeSingle();
    if (data?.id) {
      registerWorkspacesForSession(sess.user_id, [data]);
      await store.setActive(sess.user_id);
      return { ok: true };
    }
  }

  // 3. Check stale sessions via cache (token expired — can't probe DB).
  for (const sess of sessions.filter((s) => s.user_id !== activeUserId && !!s.stale)) {
    for (const entry of _wsCache.values()) {
      if (entry.slug === workspaceSlug && entry.userId === sess.user_id) {
        return { stale: true, email: sess.email };
      }
    }
  }

  return { ok: false };
}

export interface StoredSession {
  user_id: string;
  email: string;
  access_token: string;
  refresh_token: string;
  stale?: boolean;
}

interface SessionState {
  sessions: StoredSession[];
  activeUserId: string | null;
  addSession: (session: StoredSession) => void;
  setActive: (userId: string) => Promise<void>;
  removeSession: (userId: string) => void;
  removeAll: () => void;
  refreshAllSessions: () => Promise<void>;
}

/**
 * Auth-owned persisted account-session state, keyed by user id. Supabase clients
 * and workspace lookup entries are private memory-only implementation caches.
 */
export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      sessions: [],
      activeUserId: null,

      addSession: (newSession) => {
        set((s) => {
          const exists = s.sessions.some((sess) => sess.user_id === newSession.user_id);
          return {
            // Update in-place to preserve insertion order; only append for new sessions
            sessions: exists
              ? s.sessions.map((sess) =>
                  sess.user_id === newSession.user_id ? { ...newSession, stale: false } : sess
                )
              : [...s.sessions, { ...newSession, stale: false }],
            activeUserId: newSession.user_id,
          };
        });
      },

      setActive: async (userId) => {
        const session = get().sessions.find((s) => s.user_id === userId);
        if (!session) return;
        const { data } = await supabase.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        });
        if (data.session) {
          set((s) => ({
            activeUserId: userId,
            sessions: s.sessions.map((sess) =>
              sess.user_id === userId
                ? {
                    ...sess,
                    access_token: data.session!.access_token,
                    refresh_token: data.session!.refresh_token,
                    stale: false,
                  }
                : sess
            ),
          }));
        } else {
          set((s) => ({
            activeUserId: userId,
            sessions: s.sessions.map((sess) =>
              sess.user_id === userId ? { ...sess, stale: true } : sess
            ),
          }));
        }
      },

      removeSession: (userId) => {
        evictSessionClient(userId);
        evictWorkspacesForSession(userId);
        set((s) => {
          const remaining = s.sessions.filter((sess) => sess.user_id !== userId);
          const newActive =
            s.activeUserId === userId
              ? (remaining.find((sess) => !sess.stale)?.user_id ?? remaining[0]?.user_id ?? null)
              : s.activeUserId;
          return { sessions: remaining, activeUserId: newActive };
        });
      },

      removeAll: () => {
        _sessionClients.clear();
        _wsCache.clear();
        set({ sessions: [], activeUserId: null });
      },

      refreshAllSessions: async () => {
        const { sessions, activeUserId } = get();
        if (sessions.length === 0) return;

        const updated: StoredSession[] = [];
        for (const stored of sessions) {
          try {
            const tempClient = createClient(
              import.meta.env.VITE_SUPABASE_URL as string,
              import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
              { auth: isolatedAuth(`refresh-${stored.user_id}`) },
            );
            const { data, error } = await tempClient.auth.setSession({
              access_token: stored.access_token,
              refresh_token: stored.refresh_token,
            });
            if (error || !data.session) {
              if (error && !isAuthRetryableFetchError(error)) {
                evictSessionClient(stored.user_id);
                evictWorkspacesForSession(stored.user_id);
                continue;
              }
              updated.push({ ...stored, stale: true });
            } else {
              updated.push({
                user_id: data.session.user.id,
                email: data.session.user.email ?? stored.email,
                access_token: data.session.access_token,
                refresh_token: data.session.refresh_token,
                stale: false,
              });
            }
          } catch {
            updated.push({ ...stored, stale: true });
          }
        }

        const activeSurvives = updated.some((s) => s.user_id === activeUserId);
        set({
          sessions: updated,
          activeUserId: activeSurvives
            ? activeUserId
            : (updated.find((s) => !s.stale)?.user_id ?? updated[0]?.user_id ?? null),
        });

        // Restore the active session in the shared Supabase client exactly once.
        const active = updated.find((s) => s.user_id === get().activeUserId && !s.stale);
        if (active) {
          await supabase.auth.setSession({
            access_token: active.access_token,
            refresh_token: active.refresh_token,
          });
        }
      },
    }),
    {
      name: 'integritty-sessions',
    }
  )
);
